<template>
  <div class="bridge-signing-table">
    <v-card-title class="toolbar primary white--text">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Paybacks - Payout Overview
          </h1>
        </span>
      </div>
      <v-spacer />
      <v-select
        v-model="filterByStatus"
        :items="filterStatuses()"
        label="Filter By Status"
        item-key="id"
        item-text="name"
        item-value="id"
        class="mt-2"
        style="margin-right: 10px; margin-top: 28px !important; max-width: 300px;"
        dark
        color="primary"
        solo-inverted
        @change="onRefresh"
      />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-center">
            {{ props.item.payoutId }}
          </td>
          <td class="text-center">
            {{ props.item.description }}
          </td>
          <td class="text-center">
            {{ props.item.created }}
          </td>
          <td class="text-center">
            {{ props.item.confirmed }}
          </td>
          <td class="text-center">
            {{ centsToEuros(props.item.amount) }}
          </td>
          <td class="text-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                >
                  {{ getPercentage(props.item) }}
                </span>
              </template>
              <span>{{ getPercentageDetails(props.item) }}</span>
            </v-tooltip>
          </td>
          <td class="text-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="secondary"
                  v-on="on"
                  @click.stop="() => onView(props.item)"
                >
                  <v-icon>preview</v-icon>
                </v-btn>
              </template>
              <span>View Paybacks</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
    >
      {{ snackBarText }}
      <v-btn
        dark
        flat
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js';
import { centsToEuros } from '@/utils/numberFormats';

export default {
  name: 'Paybacks',
  components: {
  },
  data() {
    return {
      centsToEuros,
      filterByStatus: 'open',
      isLoading: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout ID', value: 'payoutId', align: 'center' },
        { text: 'Payout Description', value: 'description', align: 'center' },

        { text: 'Created', value: 'created', align: 'center' },
        { text: 'Confirmed', value: 'confirmed', align: 'center' },
        { text: 'Amount', value: 'amount', align: 'center' },

        { text: 'Data received', sortable: false, align: 'center' },
        { text: 'Actions', sortable: false, align: 'center' },
      ],
      snackbar: false,
      snackbarTimeout: 5000,
      snackbarColor: 'primary',
      snackBarText: '',
    };
  },
  computed: {
    list() {
      return this.$store.getters.getPaybackList;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.list :
        this.list.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    onRefresh() {
      this.$store.dispatch('fetchPatPaybackTypes');
      this.$store.dispatch('fetchPatPaybacksAll', { status: this.filterByStatus }).catch((error) => {
        this.snackbarColor = 'red';
        this.snackBarText = `Failed to load list: ${error.message}`;
        this.snackbar = true;
      });
    },
    async onView(item) {
      await this.$router.push(`/pat/payback/${item.payoutId}`);
    },
    getValues(item) {
      return {
        holdersReceived: item.holdersReceived ? item.holdersReceived : 0,
        holdersTotal: item.holdersTotal ? item.holdersTotal : 0,
      };
    },
    getPercentage(item) {
      const values = this.getValues(item);
      if (values.holdersTotal === 0) {
        return 'Unable to get total count, corrupted data?';
      }

      const percentage = new BigNumber(100).dividedBy(values.holdersTotal).multipliedBy(values.holdersReceived).toFixed(2);

      return `${percentage}%`;
    },
    getPercentageDetails(item) {
      const values = this.getValues(item);
      if (values.holdersTotal === 0) {
        return 'Please contact Tech Team if the issue persists.';
      }

      return `Holders Received: ${values.holdersReceived} / ${values.holdersTotal}`;
    },
    filterStatuses() {
      const statuses = this.statuses();
      statuses.unshift({
        id: 'open',
        name: 'Open',
      });

      return statuses;
    },
    statuses() {
      const data = this.$store.getters.getPaybackTypes;
      if (!data || !data.payback || !data.payback.status) {
        return [];
      }

      return this.transformEnumToSelectArray(data.payback.status);
    },
    transformEnumToSelectArray(options) {
      const newOptions = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(options)) {
        newOptions.push({
          id: key,
          name: value,
        });
      }

      return newOptions;
    },
  },
};
</script>
<style lang="scss">
.bridge-signing-table {
  > .toolbar {
    position: relative;
    padding: 24px 24px 32px 24px;
    margin-bottom: 32px;
  }
}

.text-center {
  text-align: center;
}

.mass-create {
  margin-right: 70px;
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/Paybacks.vue